import { useState } from 'react';
import { FileText, File, Download, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';

interface MediaPreviewProps {
  mediaUrls: string[];
  className?: string;
}

function getFileType(url: string): 'image' | 'pdf' | 'other' {
  const lowercaseUrl = url.toLowerCase();
  if (/\.(jpg|jpeg|png|gif|webp|bmp|svg)(\?|$)/.test(lowercaseUrl)) {
    return 'image';
  }
  if (/\.pdf(\?|$)/.test(lowercaseUrl)) {
    return 'pdf';
  }
  return 'other';
}

function getFileName(url: string): string {
  try {
    const pathname = new URL(url).pathname;
    const filename = pathname.split('/').pop() || 'file';
    // Decode URI component and limit length
    return decodeURIComponent(filename).slice(0, 30);
  } catch {
    return 'Attachment';
  }
}

interface ImageLightboxProps {
  src: string;
  isOpen: boolean;
  onClose: () => void;
}

function ImageLightbox({ src, isOpen, onClose }: ImageLightboxProps) {
  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-4xl p-0 bg-transparent border-none">
        <DialogTitle className="sr-only">Image Preview</DialogTitle>
        <button
          onClick={onClose}
          className="absolute top-2 right-2 z-10 p-2 rounded-full bg-foreground/60 text-background hover:bg-foreground/80 transition-colors"
        >
          <X className="h-5 w-5" />
        </button>
        <img
          src={src}
          alt="Full size preview"
          className="max-h-[80vh] w-auto mx-auto rounded-lg"
        />
      </DialogContent>
    </Dialog>
  );
}

interface MediaItemProps {
  url: string;
  onImageClick: (url: string) => void;
}

function MediaItem({ url, onImageClick }: MediaItemProps) {
  const fileType = getFileType(url);
  const fileName = getFileName(url);

  if (fileType === 'image') {
    return (
      <button
        onClick={() => onImageClick(url)}
        className="block overflow-hidden rounded-lg border border-border hover:opacity-90 transition-opacity"
      >
        <img
          src={url}
          alt="Attached image"
          className="max-w-[200px] max-h-[150px] object-cover"
          loading="lazy"
        />
      </button>
    );
  }

  if (fileType === 'pdf') {
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-2 p-2 rounded-lg border border-border bg-muted/50 hover:bg-muted transition-colors max-w-[200px]"
      >
        <FileText className="h-5 w-5 text-destructive flex-shrink-0" />
        <span className="text-xs truncate flex-1">{fileName}</span>
        <Download className="h-4 w-4 text-muted-foreground flex-shrink-0" />
      </a>
    );
  }

  // Other file types
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center gap-2 p-2 rounded-lg border border-border bg-muted/50 hover:bg-muted transition-colors max-w-[200px]"
    >
      <File className="h-5 w-5 text-muted-foreground flex-shrink-0" />
      <span className="text-xs truncate flex-1">{fileName}</span>
      <Download className="h-4 w-4 text-muted-foreground flex-shrink-0" />
    </a>
  );
}

export function MediaPreview({ mediaUrls, className }: MediaPreviewProps) {
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);

  if (!mediaUrls || mediaUrls.length === 0) return null;

  return (
    <>
      <div className={cn('flex flex-wrap gap-2 mt-2', className)}>
        {mediaUrls.map((url, index) => (
          <MediaItem
            key={`${url}-${index}`}
            url={url}
            onImageClick={setLightboxUrl}
          />
        ))}
      </div>

      <ImageLightbox
        src={lightboxUrl || ''}
        isOpen={!!lightboxUrl}
        onClose={() => setLightboxUrl(null)}
      />
    </>
  );
}
